import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radio, ShieldCheck, Clock, MessageSquareText, Trash2, ArrowRight, Loader2, Info, BarChart3, Mic, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { formatDate } from "../lib/constants";
import { ConfirmDialog } from "../components/ConfirmDialog";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)}m ${s % 60}s`);

export default function LiveHome() {
  const [consent, setConsent] = useState(false);
  const [save, setSave] = useState(true);
  const [titolo, setTitolo] = useState("");
  const [starting, setStarting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [voicesList, setVoicesList] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/live/sessions");
      setSessions(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/mypailot/voices").then(({ data }) => {
      setVoicesList(data.items);
      if (data.items.length) setVoiceId(data.items[0].id);
    }).catch(() => {});
  }, []);

  const startSession = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/live/sessions", { consenso_confermato: consent, salva_trascrizione: save, titolo: titolo || null, voice_id: voiceId || null });
      navigate(`/live/${data.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/live/sessions/${deleting.id}`);
      setSessions((s) => s.filter((x) => x.id !== deleting.id));
      toast.success("Sessione eliminata");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="live-home-page" className="space-y-10">
      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Modulo live</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Assistente Live</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Ascolta la videocall (audio della scheda + il tuo microfono), trascrive tutto in tempo reale, suggerisce cosa dire basandosi solo sulla tua Knowledge Base e a fine call genera il report con l'analisi.
        </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/live/panoramica" data-testid="live-analytics-link" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-white"><BarChart3 className="h-4 w-4" /> Panoramica call</Link>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-5">
        <section className="fade-up surface min-w-0 space-y-6 p-6 sm:p-8 lg:col-span-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20 text-primary"><Radio className="h-5 w-5" /></div>
            <div>
              <h2 className="text-base font-semibold md:text-lg">Nuova sessione</h2>
              <p className="text-xs text-muted-foreground">Desktop Chrome/Edge: scheda + microfono · Smartphone o vivavoce: solo microfono</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Titolo (opzionale)</label>
            <input data-testid="session-title-input" value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Es. Demo con Rossi SpA" className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary" />
          </div>

          <div data-testid="operator-select-block">
            <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"><UserCircle className="h-4 w-4" /> Operatore in call</label>
            {voicesList.length > 0 ? (
              <>
                <select data-testid="operator-select" value={voiceId} onChange={(e) => setVoiceId(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary">
                  <option value="">Nessuno (non riconoscere l'operatore)</option>
                  {voicesList.map((v) => (
                    <option key={v.id} value={v.id}>{v.nome}{v.ruolo ? ` — ${v.ruolo}` : ""}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-muted-foreground">La voce scelta verrà riconosciuta come "operatore"; l'altra come cliente.</p>
              </>
            ) : (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-border bg-background/60 px-4 py-3 text-xs text-muted-foreground">
                <Mic className="h-4 w-4 shrink-0" />
                <span>Nessuna voce registrata. <Link to="/mypailot" data-testid="go-mypailot-link" className="font-semibold text-primary hover:underline">Registra le voci in MyPailot</Link> per il riconoscimento dell'operatore.</span>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-amber-200">Consenso e privacy</p>
                <p className="text-muted-foreground">
                  Registrare ed elaborare una conversazione richiede il consenso dell'interlocutore. L'audio viene trascritto in streaming e
                  <strong className="text-foreground"> non viene mai conservato</strong>; la trascrizione e l'analisi vengono salvate solo se lo scegli qui sotto.
                </p>
                <label className="flex cursor-pointer items-start gap-3">
                  <input data-testid="consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" />
                  <span>Ho informato l'interlocutore e ottenuto il consenso a registrare ed elaborare la conversazione.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <input data-testid="save-transcript-checkbox" type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" />
                  <span>Salva trascrizione e analisi post-chiamata a fine sessione <span className="text-muted-foreground">(se disattivato, tutto viene cancellato al termine)</span></span>
                </label>
              </div>
            </div>
          </div>

          <button
            data-testid="start-session-btn"
            onClick={startSession}
            disabled={!consent || starting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-base font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8] disabled:opacity-40 disabled:hover:translate-y-0 sm:w-auto"
          >
            {starting ? <Loader2 className="h-5 w-5 spin-slow" /> : <Radio className="h-5 w-5" />} Avvia sessione live
          </button>
        </section>

        <div className="min-w-0 space-y-6 lg:col-span-2">
          <section className="fade-up surface min-w-0 space-y-4 p-6">
          <div className="flex items-center gap-2 text-sm font-semibold"><Info className="h-4 w-4 text-primary" /> Come funziona</div>
          <ol className="space-y-3 text-sm text-muted-foreground">
            {[
              "Avvia la sessione: scegli la scheda della videocall (Meet, Zoom, Teams…) spuntando \"Condividi audio\" e consenti il microfono. Da smartphone usa \"Solo microfono\".",
              "Il parlato di entrambi scorre in tempo reale; se hai scelto un operatore, la sua voce viene riconosciuta come \"Io\" automaticamente.",
              "Apri la \"Finestra coach\": resta sopra Meet/Zoom e mostra il suggerimento dalla KB con bordo rosso (parla ora) o verde (lascia parlare).",
              "Termina la sessione: in pochi secondi ottieni trascrizione, analisi (tono, cosa avresti potuto dire, errori), valutazione e follow-up.",
            ].map((t, i) => (
              <li key={i} className="flex gap-3"><span className="font-mono text-primary">{i + 1}.</span><span>{t}</span></li>
            ))}
          </ol>
          <p className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">I suggerimenti derivano solo dalla tua Knowledge Base: se non contiene nulla di pertinente, te lo diciamo invece di inventare.</p>
          </section>
        </div>
      </div>

      <section className="fade-up">
        <h2 className="text-base font-semibold md:text-lg">Sessioni passate</h2>
        {sessions.length === 0 ? (
          <div data-testid="sessions-empty" className="surface mt-4 p-10 text-center text-sm text-muted-foreground">Nessuna sessione ancora. Avvia la prima qui sopra.</div>
        ) : (
          <ul data-testid="sessions-list" className="stagger mt-4 space-y-2">
            {sessions.map((s) => (
              <li key={s.id} data-testid={`session-row-${s.id}`} className="surface surface-hover group flex items-center gap-4 p-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.stato === "attiva" ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                  <Radio className={`h-5 w-5 ${s.stato === "attiva" ? "pulse-dot" : ""}`} />
                </div>
                <Link to={`/live/${s.id}`} data-testid={`session-link-${s.id}`} className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{s.titolo || `Sessione del ${formatDate(s.started_at)}`}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{formatDate(s.started_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDur(s.durata_sec)}</span>
                    <span className="inline-flex items-center gap-1"><MessageSquareText className="h-3 w-3" />{s.n_suggerimenti} sugg.</span>
                    {s.stato === "attiva" ? <span className="text-emerald-400">in corso</span> : s.report_stato === "done" ? <span className="text-primary">analisi pronta</span> : null}
                  </p>
                </Link>
                <button data-testid={`session-delete-${s.id}`} onClick={() => setDeleting(s)} className="rounded-md p-2 text-muted-foreground opacity-60 transition-opacity hover:bg-secondary hover:text-red-400 group-hover:opacity-100" title="Elimina">
                  <Trash2 className="h-4 w-4" />
                </button>
                <Link to={`/live/${s.id}`} className="text-muted-foreground transition-transform group-hover:translate-x-1"><ArrowRight className="h-4 w-4" /></Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog open={!!deleting} title="Eliminare questa sessione?" description="Trascrizione, suggerimenti e analisi verranno eliminati definitivamente." onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
}
